import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
} from '@angular/core';
import { Applications, APP_NAME } from '@freelancer/config';
import { Assets } from '../assets';
import { FreelancerBreakpoints } from '../breakpoints';
import type { BackgroundColourType } from './logo.types';
import { BackgroundColor, LogoSize } from './logo.types';

@Component({
  selector: 'fl-logo',
  template: `
    <picture>
      <source
        *ngIf="logoSrcDesktop"
        [media]="FreelancerBreakpoints.DESKTOP_SMALL"
        [srcset]="logoSrcDesktop"
      />
      <source
        *ngIf="logoSrcTablet"
        [media]="FreelancerBreakpoints.TABLET"
        [srcset]="logoSrcTablet"
      />
      <source
        *ngIf="logoSrc"
        [srcset]="logoSrc"
      />
      <img
        class="LogoImg"
        alt="Freelancer logo"
        i18n-alt="Freelancer logo alt text"
        [src]="logoSrc"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.id]="id"
      />
    </picture>
  `,
  styleUrls: ['./logo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogoComponent implements OnInit, OnChanges {
  FreelancerBreakpoints = FreelancerBreakpoints;
  LogoSize = LogoSize;

  @Input() size = LogoSize.MID;
  @Input() sizeTablet?: LogoSize;
  @Input() sizeDesktop?: LogoSize;
  @Input() backgroundColor: BackgroundColor | BackgroundColourType =
    BackgroundColor.LIGHT;
  @Input() id: string;

  logoSrc: string;
  logoSrcTablet?: string;
  logoSrcDesktop?: string;

  constructor(
    @Inject(APP_NAME) private appName: Applications,
    private assets: Assets,
  ) {}

  // FIXME: T267853 - this shouldn't be necessary, but with OnPush ngOnChanges never
  // gets called on init if no input property is set (which is a valid use
  // case).
  ngOnInit(): void {
    this.getLogoSize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('size' in changes) {
      this.getLogoSize();
    }

    if ('sizeTablet' in changes) {
      this.getLogoSizeTablet();
    }

    if ('sizeDesktop' in changes) {
      this.getLogoSizeDesktop();
    }
  }

  generateLogoSize(size: LogoSize | undefined): string | undefined {
    switch (size) {
      case LogoSize.ICON:
      case LogoSize.MINI:
        return (this.logoSrc =
          this.backgroundColor === BackgroundColor.LIGHT
            ? this.appName === 'main'
              ? this.assets.getUrl('freelancer-logo-icon.svg')
              : this.assets.getUrl('freelancer-logo-icon.svg')
            : this.assets.getUrl('freelancer-logo-icon-light.svg'));
      case LogoSize.SMALL:
      case LogoSize.MID:
        return this.backgroundColor === BackgroundColor.LIGHT
          ? this.appName === 'main'
            ? this.assets.getUrl('freelancer-logo.svg')
            : this.assets.getUrl('freelancer-logo.svg')
          : this.appName === 'main'
          ? this.assets.getUrl('freelancer-logo-light.svg')
          : this.assets.getUrl('freelancer-logo-light.svg');
      default:
        return undefined;
    }
  }

  getLogoSize(): void {
    this.logoSrc =
      this.generateLogoSize(this.size) ||
      (this.appName === 'main'
        ? this.assets.getUrl('freelancer-logo.svg')
        : this.assets.getUrl('freelancer-logo.svg'));
  }

  getLogoSizeTablet(): void {
    this.logoSrcTablet = this.generateLogoSize(this.sizeTablet);
  }

  getLogoSizeDesktop(): void {
    this.logoSrcDesktop = this.generateLogoSize(this.sizeDesktop);
  }
}
